/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import ShipmentService from '../services/ShipmentService'
import DeliveryAssociateService from '../services/DeliveryAssociateService'
import Swal from 'sweetalert2'

const ShipmentDetails = () => {
    const { currentUser } = useContext(AppContext)
    const { shipmentId } = useParams()
    const [shipment, setShipment] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [showCarrierModal, setShowCarrierModal] = useState(false)
    const [showDeliveryModal, setShowDeliveryModal] = useState(false)
    const [showAssignDeliveryModal, setShowAssignDeliveryModal] = useState(false)
    const [deliveryAssociates, setDeliveryAssociates] = useState([])
    const [selectedDeliveryAssociate, setSelectedDeliveryAssociate] = useState('')
    
    // Carrier form state
    const [carrierForm, setCarrierForm] = useState({
        carrierId: 1,
        title: '',
        trackNumber: ''
    })

    // Delivery form state
    const [deliveryForm, setDeliveryForm] = useState({
        customerNote: ''
    })

    useEffect(() => {
        if (shipmentId) {
            fetchShipmentDetails()
            fetchDeliveryAssociates()
        }
    }, [shipmentId])

    const fetchShipmentDetails = async () => {
        setLoading(true)
        try {
            const response = await ShipmentService.getShipmentDetails(shipmentId)
            if (response.success) {
                setShipment(response.data)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch shipment details',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error fetching shipment details:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error fetching shipment details',
                confirmButtonColor: '#d33'
            })
        } finally {
            setLoading(false)
        }
    }

    const fetchDeliveryAssociates = async () => {
        try {
            const response = await DeliveryAssociateService.getDeliveryAssociatesDropdown()
            if (response.success) {
                setDeliveryAssociates(response.data)
            }
        } catch (error) {
            console.error('Error fetching delivery associates:', error)
        }
    }

    const handleCarrierSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)

        try {
            const response = await ShipmentService.addCarrier({
                salesShipmentId: shipmentId,
                ...carrierForm
            })

            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Carrier information added successfully!',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                setShowCarrierModal(false)
                setCarrierForm({ carrierId: 1, title: '', trackNumber: '' })
                fetchShipmentDetails() // Refresh data
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to add carrier information',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error adding carrier:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error adding carrier information',
                confirmButtonColor: '#d33'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeliverySubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)

        try {
            const response = await ShipmentService.markAsDelivered(shipmentId, deliveryForm)

            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Shipment marked as delivered successfully!',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                setShowDeliveryModal(false)
                setDeliveryForm({ customerNote: '' })
                fetchShipmentDetails() // Refresh data
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to mark shipment as delivered',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error marking as delivered:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error marking shipment as delivered',
                confirmButtonColor: '#d33'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteCarrier = async (trackId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        })

        if (result.isConfirmed) {
            try {
                const response = await ShipmentService.deleteCarrier(trackId)
                if (response.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Carrier information has been deleted.',
                        confirmButtonColor: '#3085d6',
                        timer: 2000,
                        showConfirmButton: false
                    })
                    fetchShipmentDetails() // Refresh data
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.errorMessage || 'Failed to delete carrier information',
                        confirmButtonColor: '#d33'
                    })
                }
            } catch (error) {
                console.error('Error deleting carrier:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error deleting carrier information',
                    confirmButtonColor: '#d33'
                })
            }
        }
    }

    const handlePrintShipment = async () => {
        try {
            const response = await ShipmentService.getShipmentPrint(shipmentId)
            if (response.success) {
                // Open print window with shipment data
                const printWindow = window.open('', '_blank')
                printWindow.document.write(`
                    <html>
                        <head>
                            <title>Shipment Print</title>
                            <style>
                                body { font-family: Arial, sans-serif; margin: 20px; }
                                .header { text-align: center; margin-bottom: 30px; }
                                .shipment-info { margin-bottom: 20px; }
                                .address-section { margin-bottom: 20px; }
                                .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                                .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                                .totals { text-align: right; font-weight: bold; }
                            </style>
                        </head>
                        <body>
                            <div class="header">
                                <h1>Shipment Details</h1>
                                <p>Shipment No: ${response.data.shipmentNo}</p>
                                <p>Date: ${response.data.shipmentDate}</p>
                            </div>
                            <div class="shipment-info">
                                <h3>Order Information</h3>
                                <p><strong>Order No:</strong> ${response.data.order?.orderNo}</p>
                                <p><strong>Customer:</strong> ${response.data.order?.customer?.fullName}</p>
                                <p><strong>Total Qty:</strong> ${response.data.totalQty}</p>
                            </div>
                            <div class="address-section">
                                <h3>Shipping Address</h3>
                                <p>${response.data.salesShippingAddress?.fullName}</p>
                                <p>${response.data.salesShippingAddress?.street}</p>
                                <p>${response.data.salesShippingAddress?.city}, ${response.data.salesShippingAddress?.region}</p>
                                <p>Phone: ${response.data.salesShippingAddress?.phoneNumber}</p>
                            </div>
                            <table class="items-table">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th>Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${response.data.sales_shipment_items?.map(item => `
                                        <tr>
                                            <td>${item.product?.name}</td>
                                            <td>${item.product?.sku}</td>
                                            <td>${item.qty}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                            <div class="totals">
                                <p>Total Items: ${response.data.sales_shipment_items?.length || 0}</p>
                                <p>Total Quantity: ${response.data.totalQty}</p>
                            </div>
                        </body>
                    </html>
                `)
                printWindow.document.close()
                printWindow.print()
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load shipment data for printing',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error printing shipment:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error loading shipment data for printing',
                confirmButtonColor: '#d33'
            })
        }
    }

    const getStatusBadge = (status) => {
        const statusInfo = ShipmentService.formatShipmentStatus(status)
        return `badge-${statusInfo.color}`
    }

    const formatDate = (dateString) => {
        return ShipmentService.formatDate(dateString)
    }

    const handleAssignDeliveryAssociate = async (e) => {
        e.preventDefault()
        if (!selectedDeliveryAssociate) return

        setSubmitting(true)
        try {
            const response = await DeliveryAssociateService.assignToShipment(
                shipment.sales_shipment_id, 
                selectedDeliveryAssociate
            )
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Delivery associate assigned successfully!',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                setShowAssignDeliveryModal(false)
                setSelectedDeliveryAssociate('')
                fetchShipmentDetails() // Refresh data
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to assign delivery associate',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error assigning delivery associate:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.errorMessage || 'Failed to assign delivery associate',
                confirmButtonColor: '#d33'
            })
        } finally {
            setSubmitting(false)
        }
    }

    const handleRemoveDeliveryAssociate = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You want to remove the delivery associate from this shipment?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        })

        if (result.isConfirmed) {
            try {
                const response = await DeliveryAssociateService.removeFromShipment(shipment.sales_shipment_id)
                if (response.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Removed!',
                        text: 'Delivery associate has been removed.',
                        confirmButtonColor: '#3085d6',
                        timer: 2000,
                        showConfirmButton: false
                    })
                    fetchShipmentDetails() // Refresh data
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.errorMessage || 'Failed to remove delivery associate',
                        confirmButtonColor: '#d33'
                    })
                }
            } catch (error) {
                console.error('Error removing delivery associate:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to remove delivery associate',
                    confirmButtonColor: '#d33'
                })
            }
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading shipment details...</p>
            </div>
        )
    }

    if (!shipment) {
        return (
            <div className="text-center py-5">
                <div className="text-muted">
                    <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
                    <p>Shipment not found</p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="container-fluid">
                {/* Page Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <div className="d-flex align-items-center">
                            <Link to="/shipments" className="btn btn-link p-0 me-3">
                                <i className="fas fa-arrow-left"></i>
                            </Link>
                            <div>
                                <h1 className="h3 mb-0">Shipment Details</h1>
                                <p className="text-muted mb-0">Shipment #{shipment.shipmentNo}</p>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={handlePrintShipment}
                        >
                            <i className="fas fa-print me-2"></i>Print
                        </button>
                        {shipment.shipmentStatus !== 1 && (
                            <button
                                className="btn btn-success"
                                onClick={() => setShowDeliveryModal(true)}
                            >
                                <i className="fas fa-check me-2"></i>Mark Delivered
                            </button>
                        )}
                    </div>
                </div>

                <div className="row">
                    {/* Left Column */}
                    <div className="col-lg-8">
                        {/* Shipment Information */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Shipment No:</strong></td>
                                                    <td>{shipment.shipmentNo}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Order No:</strong></td>
                                                    <td>
                                                        <Link to={`/sales-orders/${shipment.orderId}`}>
                                                            {shipment.order?.orderNo}
                                                        </Link>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Shipment Date:</strong></td>
                                                    <td>{formatDate(shipment.shipmentDate)}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Status:</strong></td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(shipment.shipmentStatus)}`}>
                                                            {ShipmentService.formatShipmentStatus(shipment.shipmentStatus).label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Customer:</strong></td>
                                                    <td>{shipment.order?.customer?.fullName}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Email:</strong></td>
                                                    <td>{shipment.order?.customer?.email}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Phone:</strong></td>
                                                    <td>{shipment.order?.customer?.mobileNo}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Total Qty:</strong></td>
                                                    <td>{parseInt(shipment.totalQty) || 0}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shipment Items */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Items</h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>SKU</th>
                                                <th>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {shipment.sales_shipment_items?.map((item) => (
                                                <tr key={item.sales_shipment_item_id}>
                                                    <td>
                                                        <div>
                                                            <strong>{item.product?.name}</strong>
                                                        </div>
                                                    </td>
                                                    <td>{item.product?.sku}</td>
                                                    <td>
                                                        <span className="badge bg-info">
                                                            {parseInt(item.qty) || 0}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Tracking Information */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Tracking Information</h5>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowCarrierModal(true)}
                                    >
                                        <i className="fas fa-plus me-2"></i>Add Tracking
                                    </button>
                                </div>
                            </div>
                            <div className="card-body">
                                {shipment.sales_shipment_track && shipment.sales_shipment_track.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>Carrier</th>
                                                    <th>Title</th>
                                                    <th>Tracking Number</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shipment.sales_shipment_track.map((track) => (
                                                    <tr key={track.sales_shipment_track_id}>
                                                        <td>{track.carrier?.name || 'N/A'}</td>
                                                        <td>{track.title}</td>
                                                        <td>
                                                            <code>{track.trackNumber}</code>
                                                        </td>
                                                        <td>
                                                                                                                    <button
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => handleDeleteCarrier(track.sales_shipment_track_id)}
                                                        >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-truck fa-3x mb-3"></i>
                                        <p>No tracking information available</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Delivery Associate */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">Delivery Associate</h5>
                                    {!shipment.delivery_associate ? (
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => setShowAssignDeliveryModal(true)}
                                        >
                                            <i className="fas fa-user-plus me-2"></i>Assign Associate
                                        </button>
                                    ) : (
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={handleRemoveDeliveryAssociate}
                                        >
                                            <i className="fas fa-user-times me-2"></i>Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                {shipment.delivery_associate ? (
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                                                <i className="fas fa-user fa-lg"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="mb-1">{shipment.delivery_associate.name}</h6>
                                            <p className="text-muted mb-0">
                                                <i className="fas fa-phone me-1"></i>
                                                {DeliveryAssociateService.formatPhoneNumber(shipment.delivery_associate.phone)}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-user-slash fa-3x mb-3"></i>
                                        <p>No delivery associate assigned</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-lg-4">
                        {/* Customer Information */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Customer Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <h6>Billing Address</h6>
                                    {shipment.sales_sip_billing_address ? (
                                        <div className="small">
                                            <div>{shipment.sales_sip_billing_address.fullName}</div>
                                            <div>{shipment.sales_sip_billing_address.street}</div>
                                            <div>{shipment.sales_sip_billing_address.city}, {shipment.sales_sip_billing_address.region}</div>
                                            <div>Phone: {shipment.sales_sip_billing_address.phoneNumber}</div>
                                        </div>
                                    ) : (
                                        <div className="text-muted">No billing address</div>
                                    )}
                                </div>
                                <div>
                                    <h6>Shipping Address</h6>
                                    {shipment.sales_shipping_address ? (
                                        <div className="small">
                                            <div>{shipment.sales_shipping_address.fullName}</div>
                                            <div>{shipment.sales_shipping_address.street}</div>
                                            <div>{shipment.sales_shipping_address.city}, {shipment.sales_shipping_address.region}</div>
                                            <div>Phone: {shipment.sales_shipping_address.phoneNumber}</div>
                                        </div>
                                    ) : (
                                        <div className="text-muted">No shipping address</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Shipment Totals */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipment Summary</h5>
                            </div>
                            <div className="card-body">
                                <table className="table table-borderless">
                                    <tbody>
                                        <tr>
                                            <td>Total Items:</td>
                                            <td className="text-end">{shipment.sales_shipment_items?.length || 0}</td>
                                        </tr>
                                        <tr>
                                            <td>Total Quantity:</td>
                                            <td className="text-end">{parseInt(shipment.totalQty) || 0}</td>
                                        </tr>
                                        <tr className="border-top">
                                            <th>Order Total:</th>
                                            <th className="text-end">{ShipmentService.formatCurrency(shipment.order?.grandTotal)}</th>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Customer Notes */}
                        {shipment.customerNote && (
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Customer Notes</h5>
                                </div>
                                <div className="card-body">
                                    <p className="mb-0">{shipment.customerNote}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Carrier Modal */}
            {showCarrierModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Tracking Information</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowCarrierModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleCarrierSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Carrier</label>
                                        <select
                                            className="form-select"
                                            value={carrierForm.carrierId}
                                            onChange={(e) => setCarrierForm({...carrierForm, carrierId: e.target.value})}
                                            required
                                        >
                                            <option value="1">Standard Shipping</option>
                                            <option value="2">Express Delivery</option>
                                            <option value="3">Courier Service</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={carrierForm.title}
                                            onChange={(e) => setCarrierForm({...carrierForm, title: e.target.value})}
                                            placeholder="Enter tracking title"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Tracking Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={carrierForm.trackNumber}
                                            onChange={(e) => setCarrierForm({...carrierForm, trackNumber: e.target.value})}
                                            placeholder="Enter tracking number"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowCarrierModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Adding...
                                            </>
                                        ) : (
                                            'Add Tracking'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Mark Delivered Modal */}
            {showDeliveryModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Mark as Delivered</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowDeliveryModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleDeliverySubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Delivery Comments</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={deliveryForm.customerNote}
                                            onChange={(e) => setDeliveryForm({...deliveryForm, customerNote: e.target.value})}
                                            placeholder="Enter delivery comments..."
                                            required
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowDeliveryModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-success"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Marking...
                                            </>
                                        ) : (
                                            'Mark as Delivered'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Delivery Associate Modal */}
            {showAssignDeliveryModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Assign Delivery Associate</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowAssignDeliveryModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleAssignDeliveryAssociate}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Select Delivery Associate</label>
                                        <select
                                            className="form-select"
                                            value={selectedDeliveryAssociate}
                                            onChange={(e) => setSelectedDeliveryAssociate(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Select Associate --</option>
                                            {deliveryAssociates.map((associate) => (
                                                <option 
                                                    key={associate.id_delivery_associate} 
                                                    value={associate.id_delivery_associate}
                                                >
                                                    {associate.name} - {associate.phone}
                                                </option>
                                            ))}
                                        </select>
                                        {deliveryAssociates.length === 0 && (
                                            <div className="form-text text-muted">
                                                No delivery associates available. 
                                                <Link to="/delivery-associates/create" className="ms-1">
                                                    Create one first
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowAssignDeliveryModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-success"
                                        disabled={submitting || !selectedDeliveryAssociate}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Assigning...
                                            </>
                                        ) : (
                                            'Assign Associate'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ShipmentDetails 